import type {
  ArtifactFilters,
  MemoryArtifact,
  MemoryRelation,
  MemoryRepository,
  MemoryUniverse,
} from './types'

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const levenshtein = (left: string, right: string): number => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let row = 1; row <= left.length; row += 1) {
    let diagonal = previous[0]!
    previous[0] = row
    for (let column = 1; column <= right.length; column += 1) {
      const above = previous[column]!
      const next = Math.min(
        previous[column - 1]! + 1,
        above + 1,
        diagonal + (left[row - 1] === right[column - 1] ? 0 : 1),
      )
      diagonal = above
      previous[column] = next
    }
  }
  return previous[right.length]!
}

const matchesFilters = (artifact: MemoryArtifact, filters: ArtifactFilters): boolean => {
  if (filters.projectId && artifact.projectId !== filters.projectId) return false
  if (filters.clusterId && artifact.clusterId !== filters.clusterId) return false
  if (filters.types?.length && !filters.types.includes(artifact.type)) return false
  return !filters.tags?.length || filters.tags.every((tag) => artifact.tags.includes(tag))
}

export const createMemoryRepository = (universe: MemoryUniverse): MemoryRepository => {
  const projectsById = new Map(universe.projects.map((project) => [project.id, project]))
  const clustersById = new Map(universe.clusters.map((cluster) => [cluster.id, cluster]))
  const artifactsById = new Map(universe.artifacts.map((artifact) => [artifact.id, artifact]))
  const relationsById = new Map(universe.relations.map((relation) => [relation.id, relation]))
  const artifactIdsByProject = new Map<string, string[]>()
  const artifactIdsByCluster = new Map<string, string[]>()
  const outgoingRelationIds = new Map<string, string[]>()
  const incomingRelationIds = new Map<string, string[]>()

  for (const artifact of universe.artifacts) {
    const projectItems = artifactIdsByProject.get(artifact.projectId) ?? []
    projectItems.push(artifact.id)
    artifactIdsByProject.set(artifact.projectId, projectItems)
    const clusterItems = artifactIdsByCluster.get(artifact.clusterId) ?? []
    clusterItems.push(artifact.id)
    artifactIdsByCluster.set(artifact.clusterId, clusterItems)
  }
  for (const relation of universe.relations) {
    const outgoing = outgoingRelationIds.get(relation.sourceId) ?? []
    outgoing.push(relation.id)
    outgoingRelationIds.set(relation.sourceId, outgoing)
    const incoming = incomingRelationIds.get(relation.targetId) ?? []
    incoming.push(relation.id)
    incomingRelationIds.set(relation.targetId, incoming)
  }

  const filterArtifacts = (filters: ArtifactFilters = {}): MemoryArtifact[] =>
    universe.artifacts.filter((artifact) => matchesFilters(artifact, filters))

  return {
    universe,
    projectsById,
    clustersById,
    artifactsById,
    relationsById,
    artifactIdsByProject,
    artifactIdsByCluster,
    outgoingRelationIds,
    incomingRelationIds,
    getArtifact: (id) => artifactsById.get(id),
    getRelationsFor: (id) => {
      const ids = [...(outgoingRelationIds.get(id) ?? []), ...(incomingRelationIds.get(id) ?? [])]
      return ids.map((relationId) => relationsById.get(relationId)).filter((item): item is MemoryRelation => Boolean(item))
    },
    filterArtifacts,
    search: (query, filters = {}) => {
      const terms = normalize(query).split(' ').filter(Boolean)
      if (!terms.length) return filterArtifacts(filters)
      return filterArtifacts(filters)
        .map((artifact) => {
          const project = projectsById.get(artifact.projectId)
          const cluster = clustersById.get(artifact.clusterId)
          const haystack = normalize(`${artifact.id} ${artifact.path} ${artifact.title} ${artifact.content} ${artifact.tags.join(' ')} ${project?.name ?? ''} ${cluster?.name ?? ''}`)
          const words = haystack.split(' ')
          const score = terms.reduce((total, term) => {
            if (haystack.includes(term)) return total + 3
            return total + (words.some((word) => levenshtein(word, term) <= Math.max(1, Math.floor(term.length / 3))) ? 1 : 0)
          }, 0)
          return { artifact, score }
        })
        .filter(({ score }) => score === terms.length * 3 || score >= terms.length)
        .sort((left, right) => right.score - left.score || left.artifact.id.localeCompare(right.artifact.id))
        .map(({ artifact }) => artifact)
    },
  }
}
